import {
  FileArchiveIcon,
  FileCodeIcon,
  FileIcon as GenericFileIcon,
  FileImageIcon,
  FileMusicIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoIcon,
  PresentationIcon,
} from 'lucide-react';

import { getFileKind, type FileKind } from '@/shared/lib/files';
import { cn } from '@/shared/lib/cn';
import type { IconComponent } from '@/shared/registry/types';

const KIND_ICON: Record<FileKind, IconComponent> = {
  image: FileImageIcon,
  video: FileVideoIcon,
  audio: FileMusicIcon,
  pdf: FileTextIcon,
  document: FileTextIcon,
  spreadsheet: FileSpreadsheetIcon,
  presentation: PresentationIcon,
  archive: FileArchiveIcon,
  code: FileCodeIcon,
  text: FileTextIcon,
  other: GenericFileIcon,
};

/** Tinted per kind; hues are tokens-free on purpose (they read well on both themes). */
const KIND_TINT: Record<FileKind, string> = {
  image: 'text-[oklch(0.62_0.16_300)] bg-[oklch(0.62_0.16_300/0.12)]',
  video: 'text-[oklch(0.62_0.17_20)] bg-[oklch(0.62_0.17_20/0.12)]',
  audio: 'text-[oklch(0.62_0.14_330)] bg-[oklch(0.62_0.14_330/0.12)]',
  pdf: 'text-[oklch(0.6_0.2_27)] bg-[oklch(0.6_0.2_27/0.12)]',
  document: 'text-[oklch(0.58_0.15_255)] bg-[oklch(0.58_0.15_255/0.12)]',
  spreadsheet: 'text-[oklch(0.6_0.14_150)] bg-[oklch(0.6_0.14_150/0.12)]',
  presentation: 'text-[oklch(0.68_0.15_55)] bg-[oklch(0.68_0.15_55/0.14)]',
  archive: 'text-[oklch(0.62_0.08_80)] bg-[oklch(0.62_0.08_80/0.14)]',
  code: 'text-[oklch(0.6_0.12_200)] bg-[oklch(0.6_0.12_200/0.12)]',
  text: 'text-muted-foreground bg-muted',
  other: 'text-muted-foreground bg-muted',
};

export function FileIcon({
  name,
  contentType,
  className,
  size = 'md',
}: {
  name: string;
  contentType?: string | null;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const kind = getFileKind(name, contentType);
  const Icon = KIND_ICON[kind];
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md',
        KIND_TINT[kind],
        size === 'sm' && 'size-7 [&_svg]:size-3.5',
        size === 'md' && 'size-9 [&_svg]:size-4',
        size === 'lg' && 'size-12 rounded-lg [&_svg]:size-6',
        className,
      )}
    >
      <Icon />
    </span>
  );
}
